import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./CustomerDB.css";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

const TABS = [
  { id: "home", label: "Home", icon: "🏠", desc: "Hero Section Inquiries" },
  { id: "pending", label: "Pending", icon: "⏳", desc: "Started but not paid" },
  { id: "paid", label: "Paid", icon: "✅", desc: "Confirmed bookings" },
  { id: "event", label: "Event", icon: "🎊", desc: "Service Bookings" },
  { id: "gaming", label: "Gaming", icon: "🎮", desc: "VR & PS5 Bookings" },
];

const STATUS_OPTIONS = [
  { value: "new_lead", label: "New Lead" },
  { value: "not_contacted", label: "Not Contacted" },
  { value: "interested", label: "Interested" },
  { value: "need_followup", label: "Need Follow-up" },
  { value: "need_2nd_call", label: "Need 2nd Call" },
  { value: "need_3rd_call", label: "Need 3rd Call" },
  { value: "no_response", label: "No Response" },
  { value: "pending_payment", label: "Pending Payment" },
  { value: "advance_paid", label: "Advance Paid" },
  { value: "payment_completed", label: "Payment Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "not_interested", label: "Not Interested" },
  { value: "just_enquired", label: "Just Enquired / Clicked" },
];

const DEFAULT_ADMIN_STATUS = "new_lead";

const normalizeAdminStatus = (value) => {
  const v = String(value || "").trim();
  return v ? v : DEFAULT_ADMIN_STATUS;
};

/* ═══════════════════════════════════════════════════════════════
   WhatsApp Templates
═══════════════════════════════════════════════════════════════ */
const buildWhatsAppMessage = (type, row) => {
  switch (type) {
    case "home":
      return `Hello ${row.name || "there"}! 👋\n\nThank you for your interest in *HexaPlay*! 🎉\n\n📅 Event Date: ${row.date || row.datetime || "Not specified"}\n📍 Location: ${row.location || "Not specified"}\n📞 Contact: ${row.mobile || row.whatsapp}\n\n🎭 Explore: https://thehexaplay.com/RoomCard\n\nCall us: +91 90877 75659\n\n*Team HexaPlay* 🎈`;

    case "event":
      return `Hello ${row.name || "there"}! 👋\n\nThank you for choosing *HexaPlay* for your *${row.service || "event"}*! 🎊\n\n🎊 Service: ${row.service || "—"}\n📅 Date: ${row.date || "—"}\n📍 Location: ${row.location || "—"}\n${row.message ? `💬 Message: ${row.message}` : ""}\n\nCall us: +91 90877 75659\n\n*Team HexaPlay* 🎉`;

    case "gaming":
      return `Hello ${row.name || "there"}! 👋\n\n🕹 Game: ${row.game || "—"}\n👥 Players: ${row.persons || "—"}\n⏱ Duration: ${row.hours || "—"}\n📅 Date: ${row.date || "—"}\n\n*Team HexaPlay* 🎮`;

    case "pending":
      return `Hello ${row.name || "there"}! 👋\n\n🎭 Theater: ${row.theater || "—"}\n📅 Date: ${row.date || "—"}\n⏰ Time: ${row.timeSlot || "—"}\n💰 Total: ₹${row.total?.toLocaleString() || "—"}\n\nReply YES to get payment link.\n\n*Team HexaPlay* 🎂`;

    case "paid":
      return `Hello ${row.name || "there"}! 🎉\n\nYour booking is *CONFIRMED*! ✅\n\n🎭 Theater: ${row.theater || "—"}\n📅 Date: ${row.date || "—"}\n⏰ Time: ${row.timeSlot || "—"}\n💰 Advance: ₹${row.advancePaid || 0}\n💵 Balance: ₹${((row.total || 0) - (row.advancePaid || 0)).toLocaleString()}\n\n📌 Arrive 15 min early.\n\n*Team HexaPlay* 🎭`;

    default:
      return `Hello ${row.name || "there"}! Thank you for connecting with HexaPlay.`;
  }
};

const toCsv = (rows, tab) => {
  if (!rows.length) return "";
  const commonCols = ["Submitted On", "Appointment Date", "Admin Status", "Comment"];
  let headers = [];

  if (tab === "home") {
    headers = [...commonCols, "Name", "Mobile", "Location", "Event Date"];
  } else if (tab === "event") {
    headers = [...commonCols, "Name", "Mobile", "Service", "Event Date", "Location", "Message"];
  } else if (tab === "gaming") {
    headers = [...commonCols, "Name", "WhatsApp", "Game", "Date", "Players", "Duration", "Location"];
  } else if (tab === "pending") {
    headers = [...commonCols, "Name", "WhatsApp", "Theater", "Slot", "Date", "Occasion", "Total", "Payment Status", "Order ID"];
  } else if (tab === "paid") {
    headers = [...commonCols, "Name", "WhatsApp", "Theater", "Slot", "Date", "Occasion", "Adults", "Kids", "Total", "Advance Paid", "Balance", "PhonePe Order ID", "Order ID"];
  }

  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleString("en-IN");
  };

  const escape = (v) => {
    const s = String(v ?? "").replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };

  const body = rows.map((r) => {
    const submitted = fmtDate(r.createdAt || r.timestamp);
    const apptDate = r.adminAppointmentDate || "";
    const meta = normalizeAdminStatus(r.adminStatus);
    const comment = r.adminComment || "";
    const base = [submitted, apptDate, meta, comment];

    if (tab === "home") return [...base, r.name, r.mobile || r.whatsapp, r.location, r.date || r.datetime || ""];
    if (tab === "event") return [...base, r.name, r.mobile, r.service, r.date, r.location, r.message];
    if (tab === "gaming") return [...base, r.name, r.whatsapp, r.game, r.date, r.persons, r.hours, r.location];
    if (tab === "pending") return [...base, r.name, r.whatsapp, r.theater, r.timeSlot, r.date, r.occasion, r.total, r.paymentStatus, r._id];
    if (tab === "paid") {
      return [
        ...base,
        r.name,
        r.whatsapp,
        r.theater,
        r.timeSlot,
        r.date,
        r.occasion,
        r.adults,
        r.kids,
        r.total,
        r.advancePaid,
        (r.total || 0) - (r.advancePaid || 0),
        r.phonePeOrderId,
        r._id,
      ];
    }
    return base;
  });

  return [headers, ...body].map((row) => row.map(escape).join(",")).join("\n");
};

const downloadCsv = (filename, csvText) => {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const saveMetaToApi = async (activeTab, rowId, meta) => {
  const isOrder = activeTab === "pending" || activeTab === "paid";
  const endpoint = isOrder ? `${API_BASE}/orders/${rowId}` : `${API_BASE}/inquiries/${rowId}`;

  try {
    const res = await fetch(endpoint, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        adminStatus: normalizeAdminStatus(meta.status),
        adminComment: meta.comment,
        adminAppointmentDate: meta.appointmentDate,
      }),
    });

    return res.ok;
  } catch (e) {
    console.warn("API meta save failed:", e);
    return false;
  }
};

const CustomerDB = () => {
  const navigate = useNavigate();
  const saveDebounceRef = useRef({});

  const [activeTab, setActiveTab] = useState("home");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [counts, setCounts] = useState({ home: 0, pending: 0, paid: 0, event: 0, gaming: 0 });
  const [toast, setToast] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [autoSync, setAutoSync] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowMetas, setRowMetas] = useState({});

  const PAGE_SIZE = 25;

  useEffect(() => {
    if (localStorage.getItem("superAdminAuth") !== "true") {
      navigate("/superadmin/login", { replace: true });
    }
  }, [navigate]);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const buildMetasFromApiData = (data) => {
    const metas = {};
    data.forEach((row) => {
      if (!row._id) return;
      metas[row._id] = {
        status: normalizeAdminStatus(row.adminStatus),
        comment: row.adminComment || "",
        appointmentDate: row.adminAppointmentDate || "",
      };
    });
    return metas;
  };

  const fetchData = useCallback(async (tab) => {
    setLoading(true);

    try {
      let data = [];

      if (tab === "pending") {
        const [pendRes, failRes] = await Promise.all([
          fetch(`${API_BASE}/orders?paymentStatus=PENDING&limit=500`).then((r) => r.json()).catch(() => []),
          fetch(`${API_BASE}/orders?paymentStatus=FAILED&limit=500`).then((r) => r.json()).catch(() => []),
        ]);

        const toArr = (r) => (Array.isArray(r) ? r : r?.data || []);
        const seen = new Set();

        data = [...toArr(pendRes), ...toArr(failRes)].filter((o) => {
          if (!o?._id) return true;
          if (seen.has(o._id)) return false;
          seen.add(o._id);
          return true;
        });
      } else if (tab === "paid") {
        const res = await fetch(`${API_BASE}/orders?paymentStatus=COMPLETED&limit=500`);
        const json = await res.json();
        data = Array.isArray(json) ? json : json?.data || [];
      } else {
        const res = await fetch(`${API_BASE}/inquiries?type=${tab}&limit=500`);
        const json = await res.json();
        data = Array.isArray(json) ? json : json?.data || [];
      }

      setRows(data);
      setRowMetas(buildMetasFromApiData(data));
    } catch (err) {
      console.error("Fetch error:", err);
      showToast("Failed to load data", "error");
      setRows([]);
      setRowMetas({});
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const [homeRes, eventRes, gamingRes, pendRes, failRes, paidRes] = await Promise.all([
        fetch(`${API_BASE}/inquiries?type=home&limit=500`).then((r) => r.json()).catch(() => []),
        fetch(`${API_BASE}/inquiries?type=event&limit=500`).then((r) => r.json()).catch(() => []),
        fetch(`${API_BASE}/inquiries?type=gaming&limit=500`).then((r) => r.json()).catch(() => []),
        fetch(`${API_BASE}/orders?paymentStatus=PENDING&limit=500`).then((r) => r.json()).catch(() => []),
        fetch(`${API_BASE}/orders?paymentStatus=FAILED&limit=500`).then((r) => r.json()).catch(() => []),
        fetch(`${API_BASE}/orders?paymentStatus=COMPLETED&limit=500`).then((r) => r.json()).catch(() => []),
      ]);

      const toArr = (r) => (Array.isArray(r) ? r : r?.data || []);

      setCounts({
        home: toArr(homeRes).length,
        event: toArr(eventRes).length,
        gaming: toArr(gamingRes).length,
        pending: toArr(pendRes).length + toArr(failRes).length,
        paid: toArr(paidRes).length,
      });
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setExpandedRowId(null);
    fetchData(activeTab);
    fetchCounts();
  }, [activeTab, fetchData, fetchCounts]);

  useEffect(() => {
    if (!autoSync) return;

    const id = setInterval(() => {
      fetchData(activeTab);
      fetchCounts();
    }, 30_000);

    return () => clearInterval(id);
  }, [autoSync, activeTab, fetchData, fetchCounts]);

  const handleSyncRow = async (row) => {
    if (!row?._id) return;
    setSyncingId(row._id);

    try {
      const res = await fetch(`${API_BASE}/payment/sync/${row._id}`, { method: "POST" });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || "Sync failed");

      if (json.updated) {
        showToast(`✓ Status updated: ${json.before} → ${json.after}`);
        fetchData(activeTab);
        fetchCounts();
      } else {
        showToast(`Status unchanged (${json.paymentStatus || row.paymentStatus})`);
      }
    } catch (err) {
      showToast(`❌ ${err.message}`, "error");
    } finally {
      setSyncingId(null);
    }
  };

  const handleMarkPaid = async (row) => {
    if (!window.confirm(`Mark order for "${row.name || "this customer"}" as PAID?`)) return;

    try {
      const res = await fetch(`${API_BASE}/payment/mark-completed/${row._id}`, { method: "POST" });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || "Failed");

      showToast("✓ Marked as paid");
      fetchData(activeTab);
      fetchCounts();
    } catch (err) {
      showToast(`❌ ${err.message}`, "error");
    }
  };

  const updateRowMeta = useCallback(
    async (rowId, updates, immediate = false) => {
      const currentMeta = rowMetas[rowId] || {
        status: DEFAULT_ADMIN_STATUS,
        comment: "",
        appointmentDate: "",
      };

      const updatedMeta = {
        ...currentMeta,
        ...updates,
        status: normalizeAdminStatus((updates && updates.status) || currentMeta.status),
      };

      setRowMetas((prev) => ({
        ...prev,
        [rowId]: updatedMeta,
      }));

      const doSave = async () => {
        const ok = await saveMetaToApi(activeTab, rowId, updatedMeta);
        if (ok) {
          if (immediate) showToast("✓ Saved");
        } else {
          showToast("⚠️ Save failed — please retry", "error");
        }
      };

      if (immediate) {
        doSave();
      } else {
        if (saveDebounceRef.current[rowId]) {
          clearTimeout(saveDebounceRef.current[rowId]);
        }
        saveDebounceRef.current[rowId] = setTimeout(doSave, 800);
      }
    },
    [activeTab, rowMetas]
  );

  const handleStatusChange = (rowId, newStatus) => {
    updateRowMeta(rowId, { status: newStatus }, true);
  };

  const handleAppointmentDateChange = (rowId, newDate) => {
    updateRowMeta(rowId, { appointmentDate: newDate }, true);
  };

  const handleCommentChange = (rowId, newComment) => {
    updateRowMeta(rowId, { comment: newComment.slice(0, 100) }, false);
  };

  const handleCommentBlur = (rowId, comment) => {
    updateRowMeta(rowId, { comment: String(comment || "").slice(0, 100) }, true);
  };

  const renderAppointmentInput = (row, meta) => (
    <input
      type="date"
      className="cdb-appointment-input"
      value={meta.appointmentDate || ""}
      onChange={(e) => {
        e.stopPropagation();
        handleAppointmentDateChange(row._id, e.target.value);
      }}
      onClick={(e) => e.stopPropagation()}
      title="Set appointment date"
    />
  );

  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date)
      ? d
      : date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  const formatTime = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return isNaN(date)
      ? ""
      : date.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        });
  };

  const formatEventDateTime = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date)
      ? d
      : date.toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
  };

  const getStatusLabel = (value) => {
    const opt = STATUS_OPTIONS.find((s) => s.value === normalizeAdminStatus(value));
    return opt ? opt.label : "New Lead";
  };

  const getStatusColor = (value) => {
    const colors = {
      new_lead: "#2563eb",
      not_contacted: "#6b7280",
      interested: "#10b981",
      need_followup: "#f59e0b",
      need_2nd_call: "#f97316",
      need_3rd_call: "#ef4444",
      no_response: "#6b7280",
      pending_payment: "#3b82f6",
      advance_paid: "#8b5cf6",
      payment_completed: "#10b981",
      cancelled: "#ef4444",
      not_interested: "#6b7280",
      just_enquired: "#3b82f6",
    };

    return colors[normalizeAdminStatus(value)] || "#2563eb";
  };

  const handleWhatsAppClick = async (row) => {
    const phone = String(row.whatsapp || row.mobile || "").replace(/\D/g, "");
    if (!phone || phone.length < 10) {
      showToast("Invalid phone number", "error");
      return;
    }

    const phoneWithCode = phone.length === 10 ? `91${phone}` : phone;
    const message = buildWhatsAppMessage(activeTab, row);

    window.open(`https://wa.me/${phoneWithCode}?text=${encodeURIComponent(message)}`, "_blank");

    if (["home", "event", "gaming"].includes(activeTab) && row._id) {
      try {
        await fetch(`${API_BASE}/inquiries/${row._id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "contacted" }),
        });

        fetchData(activeTab);
      } catch {
        /* silent */
      }
    }

    showToast(`📤 WhatsApp opened for ${row.name || "customer"}`);
  };

  const handleDelete = async () => {
    if (!deleteModal || deleting) return;

    setDeleting(true);
    const isOrder = activeTab === "pending" || activeTab === "paid";
    const endpoint = isOrder ? `${API_BASE}/orders/${deleteModal._id}` : `${API_BASE}/inquiries/${deleteModal._id}`;

    try {
      const res = await fetch(endpoint, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      let body = {};
      try {
        body = await res.json();
      } catch {
        body = {};
      }

      if (!res.ok) throw new Error(body?.message || `Server returned ${res.status}`);

      setRows((prev) => prev.filter((r) => r._id !== deleteModal._id));
      setRowMetas((prev) => {
        const copy = { ...prev };
        delete copy[deleteModal._id];
        return copy;
      });

      showToast("✓ Record deleted successfully");
      setDeleteModal(null);

      setTimeout(() => {
        fetchData(activeTab);
        fetchCounts();
      }, 300);
    } catch (err) {
      showToast(`❌ Delete failed: ${err.message}`, "error");
    } finally {
      setDeleting(false);
    }
  };

  const filteredRows = useMemo(() => {
    let out = rows.map((r) => ({
      ...r,
      __metaStatus: normalizeAdminStatus(rowMetas[r._id]?.status || r.adminStatus),
    }));

    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
    }

    if (statusFilter !== "all") {
      out = out.filter((r) => normalizeAdminStatus(r.__metaStatus) === statusFilter);
    }

    if ((activeTab === "pending" || activeTab === "paid") && paymentStatusFilter !== "all") {
      out = out.filter((r) => (r.paymentStatus || "").toUpperCase() === paymentStatusFilter);
    }

    if (dateFrom || dateTo) {
      const from = dateFrom ? new Date(dateFrom).getTime() : 0;
      const to = dateTo ? new Date(dateTo).getTime() + 86_400_000 : Infinity;

      out = out.filter((r) => {
        const t = new Date(r.createdAt || r.timestamp).getTime();
        return !isNaN(t) ? t >= from && t <= to : true;
      });
    }

    return out;
  }, [rows, search, statusFilter, paymentStatusFilter, dateFrom, dateTo, activeTab, rowMetas]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const stats = useMemo(() => {
    if (activeTab === "paid") {
      const revenue = filteredRows.reduce((s, r) => s + (Number(r.advancePaid) || 0), 0);
      const totalValue = filteredRows.reduce((s, r) => s + (Number(r.total) || 0), 0);

      return [
        { label: "Confirmed", value: filteredRows.length, icon: "🎉", accent: "green" },
        { label: "Advance In", value: `₹${revenue.toLocaleString("en-IN")}`, icon: "💰", accent: "emerald" },
        { label: "Total Value", value: `₹${totalValue.toLocaleString("en-IN")}`, icon: "📊", accent: "blue" },
        { label: "Balance Due", value: `₹${(totalValue - revenue).toLocaleString("en-IN")}`, icon: "💵", accent: "orange" },
      ];
    }

    if (activeTab === "pending") {
      const failed = filteredRows.filter((r) => r.paymentStatus === "FAILED").length;
      const unpaid = filteredRows.filter((r) => r.paymentStatus === "PENDING").length;
      const totalValue = filteredRows.reduce((s, r) => s + (Number(r.total) || 0), 0);

      return [
        { label: "Pending", value: filteredRows.length, icon: "⏳", accent: "amber" },
        { label: "Not Paid", value: unpaid, icon: "💳", accent: "purple" },
        { label: "Failed", value: failed, icon: "❌", accent: "orange" },
        { label: "Potential", value: `₹${totalValue.toLocaleString("en-IN")}`, icon: "📈", accent: "blue" },
      ];
    }

    return [
      {
        label: "Total Records",
        value: filteredRows.length,
        icon: TABS.find((t) => t.id === activeTab)?.icon || "📋",
        accent: "blue",
      },
    ];
  }, [filteredRows, activeTab]);

  const handleExport = () => {
    const exportRows = filteredRows.map((r) => ({
      ...r,
      adminStatus: normalizeAdminStatus(rowMetas[r._id]?.status || r.adminStatus),
      adminComment: rowMetas[r._id]?.comment || r.adminComment || "",
      adminAppointmentDate: rowMetas[r._id]?.appointmentDate || r.adminAppointmentDate || "",
    }));

    const csv = toCsv(exportRows, activeTab);
    if (!csv) {
      showToast("Nothing to export", "error");
      return;
    }

    downloadCsv(`hexaplay-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    showToast(`✓ Exported ${filteredRows.length} rows`);
  };

  const clearFilters = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("all");
    setPaymentStatusFilter("all");
    setCurrentPage(1);
  };

  return (
    <div className="cdb-page">
      <header className="cdb-header">
        <div className="cdb-header-title">
          <h1>🗂️ Customer Database</h1>
          <p>Manage all customer inquiries, bookings & sessions in one place</p>
        </div>

        <div className="cdb-header-actions">
          <button className="cdb-export-btn" onClick={handleExport} title="Export to CSV">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>
        </div>
      </header>

      <div className="cdb-tabs-wrap">
        <div className="cdb-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`cdb-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => {
                setActiveTab(tab.id);
                setSearch("");
                setStatusFilter("all");
                setPaymentStatusFilter("all");
                setDateFrom("");
                setDateTo("");
              }}
            >
              <span className="cdb-tab-icon">{tab.icon}</span>
              <span className="cdb-tab-label">{tab.label}</span>
              <span className="cdb-tab-count">{counts[tab.id] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {(activeTab === "paid" || activeTab === "pending") && (
        <div className="cdb-stats-grid">
          {stats.map((s, i) => (
            <div key={i} className={`cdb-stat-card cdb-stat-${s.accent}`}>
              <div className="cdb-stat-icon">{s.icon}</div>
              <div className="cdb-stat-body">
                <span className="cdb-stat-label">{s.label}</span>
                <span className="cdb-stat-value">{s.value}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="cdb-toolbar">
        <div className="cdb-toolbar-inner">
          <div className="cdb-section-info">
            <span className="cdb-section-emoji">{TABS.find((t) => t.id === activeTab)?.icon}</span>
            <div>
              <h3>{TABS.find((t) => t.id === activeTab)?.label}</h3>
              <p>
                {TABS.find((t) => t.id === activeTab)?.desc} • <strong>{filteredRows.length}</strong> records
              </p>
            </div>
          </div>

          <div className="cdb-toolbar-controls">
            <div className="cdb-search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>

              <input
                type="text"
                placeholder="Search name, phone, location…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />

              {search && (
                <button onClick={() => setSearch("")} className="cdb-search-clear">
                  ✕
                </button>
              )}
            </div>

            <select
              className="cdb-filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Lead Status</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {(activeTab === "pending" || activeTab === "paid") && (
              <select
                className="cdb-filter-select"
                value={paymentStatusFilter}
                onChange={(e) => {
                  setPaymentStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Payment</option>
                {activeTab === "pending" && (
                  <>
                    <option value="PENDING">Pending</option>
                    <option value="FAILED">Failed</option>
                  </>
                )}
                {activeTab === "paid" && (
                  <>
                    <option value="COMPLETED">Completed</option>
                  </>
                )}
              </select>
            )}

            <input
              type="date"
              className="cdb-date-input"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setCurrentPage(1);
              }}
              title="From date"
            />

            <input
              type="date"
              className="cdb-date-input"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setCurrentPage(1);
              }}
              title="To date"
            />

            {(search || dateFrom || dateTo || statusFilter !== "all" || paymentStatusFilter !== "all") && (
              <button className="cdb-clear-btn" onClick={clearFilters}>
                Clear
              </button>
            )}

            <button
              className="cdb-refresh-btn"
              onClick={() => {
                fetchData(activeTab);
                fetchCounts();
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              Refresh
            </button>

            {activeTab === "pending" && (
              <label className="cdb-autosync-toggle" title="Auto-refresh every 30s">
                <input type="checkbox" checked={autoSync} onChange={(e) => setAutoSync(e.target.checked)} />
                <span>Auto-sync</span>
              </label>
            )}
          </div>
        </div>

        {activeTab === "pending" && lastSync && (
          <div className="cdb-last-sync">Last sync: {lastSync.toLocaleTimeString("en-IN")}</div>
        )}
      </div>

      <div className="cdb-table-wrap">
        {loading ? (
          <div className="cdb-loading">
            <div className="cdb-spinner" />
            <p>Loading records...</p>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="cdb-empty">
            <div className="cdb-empty-icon">📭</div>
            <h3>No records found</h3>
            <p>
              {search || dateFrom || dateTo || statusFilter !== "all" || paymentStatusFilter !== "all"
                ? "Try clearing the filters"
                : "Records will appear here when customers fill the form"}
            </p>
          </div>
        ) : (
          <>
            <div className="cdb-table-scroll">
              <table className="cdb-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}></th>
                    <th>#</th>
                    <th>Submitted On</th>

                    {activeTab === "home" && (
                      <>
                        <th>Name</th>
                        <th>Mobile</th>
                        <th>Location</th>
                        <th>Event Date</th>
                        <th>Appointment</th>
                      </>
                    )}

                    {activeTab === "event" && (
                      <>
                        <th>Name</th>
                        <th>Mobile</th>
                        <th>Service</th>
                        <th>Event Date</th>
                        <th>Appointment</th>
                        <th>Location</th>
                        <th>Message</th>
                      </>
                    )}

                    {activeTab === "gaming" && (
                      <>
                        <th>Name</th>
                        <th>WhatsApp</th>
                        <th>Game</th>
                        <th>Date</th>
                        <th>Appointment</th>
                        <th>Players</th>
                        <th>Duration</th>
                        <th>Location</th>
                      </>
                    )}

                    {activeTab === "pending" && (
                      <>
                        <th>Name</th>
                        <th>WhatsApp</th>
                        <th>Theater</th>
                        <th>Slot</th>
                        <th>Date</th>
                        <th>Total</th>
                        <th>Payment</th>
                        <th>Appointment</th>
                      </>
                    )}

                    {activeTab === "paid" && (
                      <>
                        <th>Name</th>
                        <th>WhatsApp</th>
                        <th>Theater</th>
                        <th>Slot</th>
                        <th>Date</th>
                        <th>Occasion</th>
                        <th>Paid</th>
                        <th>Balance</th>
                        <th>Order ID</th>
                        <th>Appointment</th>
                      </>
                    )}

                    <th>Status</th>
                    <th className="cdb-comment-col">Comment</th>
                    <th className="cdb-action-col">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {pagedRows.map((row, idx) => {
                    const globalIdx = (currentPage - 1) * PAGE_SIZE + idx;
                    const isExpanded = expandedRowId === row._id;

                    const meta = rowMetas[row._id] || {
                      status: DEFAULT_ADMIN_STATUS,
                      comment: "",
                      appointmentDate: "",
                    };

                    return (
                      <React.Fragment key={row._id || globalIdx}>
                        <tr
                          className={`cdb-row ${isExpanded ? "cdb-row-expanded" : ""}`}
                          style={{ animationDelay: `${idx * 0.02}s` }}
                          onClick={() => setExpandedRowId(isExpanded ? null : row._id)}
                        >
                          <td className="cdb-expand-cell">
                            <span className={`cdb-expand-icon ${isExpanded ? "open" : ""}`}>▸</span>
                          </td>

                          <td>
                            <span className="cdb-row-num">{globalIdx + 1}</span>
                          </td>

                          <td>
                            <div className="cdb-cell-date">
                              <span className="cdb-date-main">{formatDate(row.createdAt || row.timestamp)}</span>
                              <span className="cdb-date-time">{formatTime(row.createdAt || row.timestamp)}</span>
                            </div>
                          </td>

                          {activeTab === "home" && (
                            <>
                              <td><strong>{row.name || "—"}</strong></td>
                              <td>
                                <a href={`tel:${row.mobile || row.whatsapp}`} className="cdb-link" onClick={(e) => e.stopPropagation()}>
                                  {row.mobile || row.whatsapp || "—"}
                                </a>
                              </td>
                              <td>
                                <span className="cdb-pill cdb-pill-blue">
                                  {row.location ? row.location.charAt(0).toUpperCase() + row.location.slice(1) : "—"}
                                </span>
                              </td>
                              <td>{formatDate(row.date || row.datetime)}</td>
                              <td>{renderAppointmentInput(row, meta)}</td>
                            </>
                          )}

                          {activeTab === "event" && (
                            <>
                              <td><strong>{row.name || "—"}</strong></td>
                              <td>
                                <a href={`tel:${row.mobile}`} className="cdb-link" onClick={(e) => e.stopPropagation()}>
                                  {row.mobile || "—"}
                                </a>
                              </td>
                              <td><span className="cdb-pill cdb-pill-pink">{row.service || "—"}</span></td>
                              <td>{row.date || "—"}</td>
                              <td>{renderAppointmentInput(row, meta)}</td>
                              <td>{row.location || "—"}</td>
                              <td className="cdb-truncate" title={row.message}>{row.message || "—"}</td>
                            </>
                          )}

                          {activeTab === "gaming" && (
                            <>
                              <td><strong>{row.name || "—"}</strong></td>
                              <td>
                                <a href={`tel:${row.whatsapp}`} className="cdb-link" onClick={(e) => e.stopPropagation()}>
                                  {row.whatsapp || "—"}
                                </a>
                              </td>
                              <td><span className="cdb-pill cdb-pill-purple">{row.game || "—"}</span></td>
                              <td>{row.date || "—"}</td>
                              <td>{renderAppointmentInput(row, meta)}</td>
                              <td>{row.persons || "—"}</td>
                              <td>{row.hours || "—"}</td>
                              <td>{row.location || "—"}</td>
                            </>
                          )}

                          {activeTab === "pending" && (
                            <>
                              <td><strong>{row.name || "—"}</strong></td>
                              <td>
                                <a href={`tel:${row.whatsapp}`} className="cdb-link" onClick={(e) => e.stopPropagation()}>
                                  {row.whatsapp || "—"}
                                </a>
                              </td>
                              <td>{row.theater || "—"}</td>
                              <td><span className="cdb-pill cdb-pill-blue">{row.timeSlot || "—"}</span></td>
                              <td>{row.date || "—"}</td>
                              <td><strong>₹{row.total?.toLocaleString() || 0}</strong></td>
                              <td>
                                <span className={`cdb-pay-badge cdb-pay-${(row.paymentStatus || "PENDING").toLowerCase()}`}>
                                  {row.paymentStatus || "PENDING"}
                                </span>
                              </td>
                              <td>{renderAppointmentInput(row, meta)}</td>
                            </>
                          )}

                          {activeTab === "paid" && (
                            <>
                              <td><strong>{row.name || "—"}</strong></td>
                              <td>
                                <a href={`tel:${row.whatsapp}`} className="cdb-link" onClick={(e) => e.stopPropagation()}>
                                  {row.whatsapp || "—"}
                                </a>
                              </td>
                              <td>{row.theater || "—"}</td>
                              <td><span className="cdb-pill cdb-pill-blue">{row.timeSlot || "—"}</span></td>
                              <td>{row.date || "—"}</td>
                              <td><span className="cdb-pill">{row.occasion || "—"}</span></td>
                              <td><strong className="cdb-green">₹{row.advancePaid || 0}</strong></td>
                              <td><strong className="cdb-orange">₹{((row.total || 0) - (row.advancePaid || 0)).toLocaleString()}</strong></td>
                              <td title={row._id}><code className="cdb-code">{row._id?.slice(-6).toUpperCase()}</code></td>
                              <td>{renderAppointmentInput(row, meta)}</td>
                            </>
                          )}

                          <td>
                            <select
                              className="cdb-status-select"
                              value={normalizeAdminStatus(meta.status)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStatusChange(row._id, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                backgroundColor: getStatusColor(meta.status),
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                padding: "4px 8px",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: "pointer",
                              }}
                            >
                              {STATUS_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value} style={{ color: "#000" }}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="cdb-comment-cell" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              className="cdb-comment-input"
                              value={meta.comment || ""}
                              maxLength={100}
                              onChange={(e) => handleCommentChange(row._id, e.target.value)}
                              onBlur={(e) => handleCommentBlur(row._id, e.target.value)}
                              placeholder="Add comment (100 ch)"
                              title={`${(meta.comment || "").length}/100 characters`}
                            />
                          </td>

                          <td className="cdb-action-col" onClick={(e) => e.stopPropagation()}>
                            <div className="cdb-actions">
                              {activeTab === "pending" && (
                                <>
                                  <button
                                    className="cdb-btn-sync"
                                    onClick={() => handleSyncRow(row)}
                                    disabled={syncingId === row._id}
                                    title="Sync payment status"
                                  >
                                    {syncingId === row._id ? "⟳" : "🔄"}
                                  </button>

                                  <button className="cdb-btn-markpaid" onClick={() => handleMarkPaid(row)} title="Mark as paid manually">
                                    ✓ Paid
                                  </button>
                                </>
                              )}

                              <button className="cdb-btn-whatsapp" onClick={() => handleWhatsAppClick(row)} title="Send WhatsApp message">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                                <span>WhatsApp</span>
                              </button>

                              <button className="cdb-btn-delete" onClick={() => setDeleteModal(row)} title="Delete record">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="cdb-details-row">
                            <td colSpan={20}>
                              <div className="cdb-details">
                                <div className="cdb-details-grid">
                                  <div className="cdb-detail-item">
                                    <span className="cdb-detail-label">Record ID</span>
                                    <code className="cdb-code">{row._id}</code>
                                  </div>

                                  {activeTab === "home" && (
                                    <div className="cdb-detail-item">
                                      <span className="cdb-detail-label">Event Date</span>
                                      <span>{formatDate(row.date || row.datetime)}</span>
                                    </div>
                                  )}

                                  <div className="cdb-detail-item">
                                    <span className="cdb-detail-label">Admin Comment</span>
                                    <span>{meta.comment || "—"}</span>
                                  </div>

                                  <div className="cdb-detail-item">
                                    <span className="cdb-detail-label">Followup Status</span>
                                    <span style={{ color: getStatusColor(meta.status), fontWeight: 600 }}>
                                      {getStatusLabel(meta.status)}
                                    </span>
                                  </div>

                                  {meta.appointmentDate && (
                                    <div className="cdb-detail-item">
                                      <span className="cdb-detail-label">Next Appointment</span>
                                      <span>{formatDate(meta.appointmentDate)}</span>
                                    </div>
                                  )}

                                  {(activeTab === "pending" || activeTab === "paid") && (
                                    <>
                                      <div className="cdb-detail-item">
                                        <span className="cdb-detail-label">PhonePe Merchant ID</span>
                                        <code className="cdb-code">{row.phonePeMerchantOrderId || "—"}</code>
                                      </div>

                                      <div className="cdb-detail-item">
                                        <span className="cdb-detail-label">PhonePe Order ID</span>
                                        <code className="cdb-code">{row.phonePeOrderId || "—"}</code>
                                      </div>

                                      <div className="cdb-detail-item">
                                        <span className="cdb-detail-label">Occasion</span>
                                        <span>{row.occasion || "—"}</span>
                                      </div>

                                      <div className="cdb-detail-item">
                                        <span className="cdb-detail-label">Guests</span>
                                        <span>{row.adults || 0} adults, {row.kids || 0} kids</span>
                                      </div>

                                      <div className="cdb-detail-item">
                                        <span className="cdb-detail-label">Total</span>
                                        <strong>₹{(row.total || 0).toLocaleString()}</strong>
                                      </div>

                                      <div className="cdb-detail-item">
                                        <span className="cdb-detail-label">Advance Paid</span>
                                        <strong className="cdb-green">₹{row.advancePaid || 0}</strong>
                                      </div>

                                      <div className="cdb-detail-item">
                                        <span className="cdb-detail-label">Balance</span>
                                        <strong className="cdb-orange">₹{((row.total || 0) - (row.advancePaid || 0)).toLocaleString()}</strong>
                                      </div>

                                      {row.couponCode && row.couponCode !== "N/A" && (
                                        <div className="cdb-detail-item">
                                          <span className="cdb-detail-label">Coupon</span>
                                          <span className="cdb-pill cdb-pill-green">
                                            {row.couponCode}
                                            {row.discount ? ` (-₹${row.discount})` : ""}
                                          </span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>

                                {(activeTab === "pending" || activeTab === "paid") && (
                                  <>
                                    {(row.cakes?.length > 0 ||
                                      row.extraDecors?.length > 0 ||
                                      row.gifts?.length > 0 ||
                                      row.services?.length > 0) && (
                                      <div className="cdb-addons">
                                        <h4>Add-ons</h4>

                                        <div className="cdb-addons-grid">
                                          {row.cakes?.length > 0 && (
                                            <div>
                                              <strong>🎂 Cakes:</strong>{" "}
                                              {row.cakes.map((c) => `${c.name} (₹${c.price})`).join(", ")}
                                            </div>
                                          )}

                                          {row.extraDecors?.length > 0 && (
                                            <div>
                                              <strong>🎈 Decor:</strong>{" "}
                                              {row.extraDecors.map((d) => `${d.name} (₹${d.price})`).join(", ")}
                                            </div>
                                          )}

                                          {row.gifts?.length > 0 && (
                                            <div>
                                              <strong>🎁 Gifts:</strong>{" "}
                                              {row.gifts
                                                .map((g) => `${g.name}${g.qty > 1 ? ` x${g.qty}` : ""} (₹${g.price * (g.qty || 1)})`)
                                                .join(", ")}
                                            </div>
                                          )}

                                          {row.services?.length > 0 && (
                                            <div>
                                              <strong>⚙️ Services:</strong>{" "}
                                              {row.services.map((s) => `${s.name} (₹${s.price})`).join(", ")}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    <div className="cdb-timeline">
                                      <h4>Payment Timeline</h4>

                                      <div className="cdb-timeline-track">
                                        <div className={`cdb-tl-step ${row.createdAt ? "done" : ""}`}>
                                          <div className="cdb-tl-dot"></div>
                                          <div className="cdb-tl-label">Created</div>
                                          <div className="cdb-tl-time">{row.createdAt ? formatEventDateTime(row.createdAt) : ""}</div>
                                        </div>

                                        <div
                                          className={`cdb-tl-step ${
                                            row.paymentStatus === "INITIATED" || row.paymentStatus === "COMPLETED"
                                              ? "done"
                                              : row.paymentStatus === "FAILED"
                                              ? "failed"
                                              : ""
                                          }`}
                                        >
                                          <div className="cdb-tl-dot"></div>
                                          <div className="cdb-tl-label">Payment Initiated</div>
                                          <div className="cdb-tl-time">{row.phonePeMerchantOrderId ? "Yes" : "—"}</div>
                                        </div>

                                        <div
                                          className={`cdb-tl-step ${
                                            row.paymentStatus === "COMPLETED"
                                              ? "done"
                                              : row.paymentStatus === "FAILED"
                                              ? "failed"
                                              : ""
                                          }`}
                                        >
                                          <div className="cdb-tl-dot"></div>
                                          <div className="cdb-tl-label">
                                            {row.paymentStatus === "COMPLETED"
                                              ? "Paid ✓"
                                              : row.paymentStatus === "FAILED"
                                              ? "Failed ✗"
                                              : "Awaiting Payment"}
                                          </div>
                                          <div className="cdb-tl-time">{row.paymentStatus || "—"}</div>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="cdb-pagination">
                <span className="cdb-page-info">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
                </span>

                <div className="cdb-page-controls">
                  <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                    « First
                  </button>

                  <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                    ‹ Prev
                  </button>

                  <span className="cdb-page-current">
                    Page {currentPage} / {totalPages}
                  </span>

                  <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>
                    Next ›
                  </button>

                  <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                    Last »
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {toast && <div className={`cdb-toast cdb-toast-${toast.type}`}>{toast.message}</div>}

      {deleteModal && (
        <div className="cdb-modal-backdrop" onClick={() => !deleting && setDeleteModal(null)}>
          <div className="cdb-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🗑️ Delete Record?</h3>

            <p>
              This will permanently delete the record for <strong>{deleteModal.name || "this customer"}</strong>.{" "}
              {(activeTab === "pending" || activeTab === "paid") && <>The booked slot will be released.</>}
              <br />
              <br />
              This action cannot be undone.
            </p>

            <div className="cdb-modal-actions">
              <button className="cdb-modal-cancel" onClick={() => setDeleteModal(null)} disabled={deleting}>
                Cancel
              </button>

              <button className="cdb-modal-confirm" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDB;